import { Image, Text, View } from "react-native";
import { useMemo, useState } from "react";
import { FileText, Plus, Receipt } from "lucide-react-native";
import {
  AnimatedNumber,
  CardSurface,
  DocumentRow,
  InlineOptionPicker,
  MoonFormField,
  MoonNumericField,
  SectionTitleRow,
  Tag,
} from "@louise/moon-ui";
import {
  formatCurrency,
  formatDate,
  getCurrencyAffixes,
  type Locale,
  useTranslations,
} from "@src/packages/i18n";
import {
  MEDICAL_DEDUCTION_AGI_RATE,
  calculateMedicalExpenseDeduction,
} from "@src/packages/costs/medicalExpenseDeduction";
import type { TaxDeductionCalculation } from "@src/packages/costs/hooks/useTaxDeductions";
import type { ApiPaymentRecord } from "@src/packages/costs/hooks/usePaymentRecords";
import { colors } from "@louise/moon-ui/theme/tokens";
import { EMBER } from "../../../onboarding/theme";
import type {
  TaxReviewEntry,
  TaxReviewSummary,
} from "@src/packages/costs/taxReview";
import TaxDeductionPotentialEmptyState from "./TaxDeductionPotentialEmptyState";
import TaxExpenseEmptyState from "./TaxExpenseEmptyState";
import { TAX_EXPENSE_RECEIPT_ILLUSTRATION } from "./taxReviewAssets";
import InfoSheet, {
  InfoButton,
  type InfoSection,
} from "../../components/InfoSheet";
import {
  getTaxReviewStatusTagTone,
  getTaxReviewStatusTranslationKey,
  isItemizationChoice,
  orderTaxReviewEntries,
  type TaxCalculationLineItem,
  type ItemizationChoice,
} from "./taxReviewHelpers";
import { semanticColors } from "@louise/theme/tokens";

export {
  itemizationChoice,
  itemizationValue,
  type ItemizationChoice,
} from "./taxReviewHelpers";

type Props = {
  summary: TaxReviewSummary;
  calculation: ReturnType<typeof calculateMedicalExpenseDeduction>;
  calculationSnapshot?: TaxDeductionCalculation | null;
  locale: Locale;
  agiDraft: number | null;
  itemizationDraft: ItemizationChoice;
  inlineInputError: string | null;
  focusedId?: string;
  onAgiChange: (value: number | null) => void;
  onItemizationChange: (value: ItemizationChoice) => void;
  onAddExpense: () => void;
  onOpenDetails: (entry: TaxReviewEntry) => void;
  infoSections: InfoSection[];
  onOpenTaxInputs?: () => void;
  onOpenTaxReview?: () => void;
  /** All payment records regardless of tax year — used to surface a
   *  "Recently added" list in the empty state when the review year has none. */
  allRecords?: ApiPaymentRecord[];
  onOpenPaymentRecord?: (record: ApiPaymentRecord) => void;
};

function entryTagLabel(
  entry: TaxReviewEntry,
  t: ReturnType<typeof useTranslations>,
  lineItem?: TaxCalculationLineItem,
) {
  return t(getTaxReviewStatusTranslationKey(entry, lineItem));
}

function ReviewEntry({
  entry,
  focused,
  onOpenDetails,
  locale,
  t,
  calculationLineItem,
}: {
  entry: TaxReviewEntry;
  focused: boolean;
  onOpenDetails: () => void;
  locale: Locale;
  t: ReturnType<typeof useTranslations>;
  calculationLineItem?: TaxCalculationLineItem;
}) {
  const statusLabel = entryTagLabel(entry, t, calculationLineItem);

  return (
    <DocumentRow
      title={entry.candidate.label}
      subtitle={`${entry.candidate.provider} · ${formatDate(entry.candidate.occurredOn, locale)}`}
      leading={
        <Image
          source={TAX_EXPENSE_RECEIPT_ILLUSTRATION}
          accessibilityIgnoresInvertColors
          resizeMode="contain"
          style={{ width: 34, height: 34 }}
        />
      }
      titleRight={
        <Tag
          label={statusLabel}
          tone={getTaxReviewStatusTagTone(entry, calculationLineItem)}
        />
      }
      trailing={
        <Text
          className="font-dm-mono"
          style={{ fontSize: 12, color: EMBER.colors.text }}
        >
          {formatCurrency(entry.candidate.patientPaidAmount, locale)}
        </Text>
      }
      onPress={onOpenDetails}
      accessibilityLabel={`${entry.candidate.label}, ${statusLabel}`}
      style={
        focused
          ? {
              borderWidth: 1,
              borderColor: semanticColors.accent.default,
            }
          : undefined
      }
    />
  );
}

function RecapBullet({ children }: { children: string }) {
  return (
    <View
      style={{
        flexDirection: "row",
        alignItems: "flex-start",
        gap: 8,
      }}
    >
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 14,
          lineHeight: 18,
          color: EMBER.colors.textMid,
        }}
      >
        •
      </Text>
      <Text
        className="font-dm-sans"
        style={{
          flex: 1,
          fontSize: 12,
          lineHeight: 18,
          color: EMBER.colors.textMid,
        }}
      >
        {children}
      </Text>
    </View>
  );
}

function DeductionComparison({
  itemizedDeduction,
  standardDeduction,
  currencyAffixes,
  t,
}: {
  itemizedDeduction: number;
  standardDeduction: number;
  currencyAffixes: ReturnType<typeof getCurrencyAffixes>;
  t: ReturnType<typeof useTranslations>;
}) {
  return (
    <View style={{ flexDirection: "row", gap: 18 }}>
      <View style={{ flex: 1, minWidth: 0, gap: 3 }}>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 12,
            lineHeight: 16,
            color: EMBER.colors.textMid,
          }}
        >
          {t("costs.tax_review.itemized_deduction_amount_label")}
        </Text>
        <AnimatedNumber
          className="font-dm-mono"
          value={itemizedDeduction}
          prefix={currencyAffixes.prefix}
          suffix={currencyAffixes.suffix}
          style={{
            fontSize: 22,
            color: colors.greyBrown,
            fontWeight: "700",
          }}
        />
      </View>
      <View style={{ flex: 1, minWidth: 0, gap: 3 }}>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 12,
            lineHeight: 16,
            color: EMBER.colors.textMid,
          }}
        >
          {t("costs.tax_review.standard_deduction_amount_label")}
        </Text>
        <AnimatedNumber
          className="font-dm-mono"
          value={standardDeduction}
          prefix={currencyAffixes.prefix}
          suffix={currencyAffixes.suffix}
          style={{
            fontSize: 22,
            color: colors.greyBrown,
            fontWeight: "700",
          }}
        />
      </View>
    </View>
  );
}

export default function TaxReviewSections({
  summary,
  calculation,
  calculationSnapshot,
  locale,
  agiDraft,
  itemizationDraft,
  inlineInputError,
  focusedId,
  onAgiChange,
  onItemizationChange,
  onAddExpense,
  onOpenDetails,
  infoSections,
  onOpenTaxInputs,
  onOpenTaxReview,
  allRecords,
  onOpenPaymentRecord,
}: Props) {
  const t = useTranslations();
  const currencyAffixes = getCurrencyAffixes(locale);
  const [showInfo, setShowInfo] = useState(false);
  const calculationLineItemsByRecordId = useMemo(
    () =>
      new Map(
        (calculationSnapshot?.lineItems ?? []).map((lineItem) => [
          lineItem.paymentRecordId,
          lineItem,
        ]),
      ),
    [calculationSnapshot?.lineItems],
  );
  const orderedEntries = orderTaxReviewEntries(
    summary.entries,
    calculationLineItemsByRecordId,
  );
  const potentialDeduction =
    calculationSnapshot?.totals.potentialDeduction ??
    calculation.potentialDeduction;
  const showDeductionComparison =
    itemizationDraft === "unknown" && agiDraft != null;
  const candidates = useMemo(
    () => summary.entries.map((entry) => entry.candidate),
    [summary.entries],
  );
  const itemizedCalculation = useMemo(
    () => calculateMedicalExpenseDeduction(candidates, agiDraft, true),
    [agiDraft, candidates],
  );
  const standardCalculation = useMemo(
    () => calculateMedicalExpenseDeduction(candidates, agiDraft, false),
    [agiDraft, candidates],
  );
  const itemizedDeduction =
    calculationSnapshot?.federal.comparison.itemizedDeduction ??
    itemizedCalculation.potentialDeduction ??
    0;
  const standardDeduction =
    calculationSnapshot?.federal.comparison.standardDeduction ??
    standardCalculation.potentialDeduction ??
    0;
  const recordsAnalyzed =
    calculationSnapshot?.totals.recordsAnalyzed ?? summary.recordsAnalyzed;
  const patientPaidTotal =
    calculationSnapshot?.totals.grossPatientPaid ?? summary.patientPaidTotal;
  const offsetTotal =
    calculationSnapshot?.totals.reimbursedOrOffset ??
    calculation.potentialNonPatientAllocatedTotal;

  const handleItemizationChange = (value: string) => {
    if (!isItemizationChoice(value)) return;
    onItemizationChange(value);
  };

  return (
    <>
      <View>
        <SectionTitleRow
          title={t("costs.tax_review.your_tax_year_recap", {
            params: { tax_year: String(summary.taxYear) },
          })}
          icon={Receipt}
          style={{ marginTop: 28 }}
        />
        <CardSurface
          style={{
            padding: 18,
            gap: 14,
            flexDirection: "row",
            alignItems: "flex-start",
          }}
        >
          <View style={{ flex: 1, minWidth: 0, gap: 14 }}>
            {potentialDeduction == null && !showDeductionComparison ? (
              <TaxDeductionPotentialEmptyState
                onOpenTaxInputs={onOpenTaxInputs}
                onOpenTaxReview={onOpenTaxReview}
              />
            ) : (
              <View style={{ gap: 7 }}>
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 11,
                    color: EMBER.colors.textFaint,
                    textTransform: "uppercase",
                    letterSpacing: 0.35,
                    fontWeight: "700",
                  }}
                >
                  {t("costs.tax_review.potential_deduction")}
                </Text>
                {showDeductionComparison ? (
                  <DeductionComparison
                    itemizedDeduction={itemizedDeduction}
                    standardDeduction={standardDeduction}
                    currencyAffixes={currencyAffixes}
                    t={t}
                  />
                ) : (
                  <AnimatedNumber
                    className="font-dm-mono"
                    value={potentialDeduction ?? 0}
                    prefix={currencyAffixes.prefix}
                    suffix={currencyAffixes.suffix}
                    style={{
                      fontSize: 28,
                      color: colors.greyBrown,
                      fontWeight: "700",
                    }}
                  />
                )}
                <View style={{ gap: 5 }}>
                  <RecapBullet>
                    {t("costs.tax_review.expenses_added_basis", {
                      params: { count: String(recordsAnalyzed) },
                    })}
                  </RecapBullet>
                  <RecapBullet>
                    {`${formatCurrency(patientPaidTotal, locale)} ${t("costs.tax_review.patient_paid_costs").toLowerCase()}`}
                  </RecapBullet>
                  {offsetTotal > 0 ? (
                    <RecapBullet>
                      {t("costs.tax_review.non_patient_allocations_basis", {
                        params: {
                          amount: formatCurrency(offsetTotal, locale),
                        },
                      })}
                    </RecapBullet>
                  ) : null}
                  <RecapBullet>
                    {t("costs.tax_review.agi_threshold_explanation", {
                      params: {
                        rate: String(MEDICAL_DEDUCTION_AGI_RATE * 100),
                      },
                    })}
                  </RecapBullet>
                </View>
              </View>
            )}
          </View>

          <CardSurface
            variant="panel"
            style={{ flex: 1, minWidth: 0, gap: 14, padding: 14 }}
          >
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 8,
              }}
            >
              <Text
                className="font-dm-sans"
                style={{
                  flex: 1,
                  fontSize: 11,
                  color: EMBER.colors.textFaint,
                  textTransform: "uppercase",
                  letterSpacing: 0.35,
                  fontWeight: "700",
                }}
              >
                {t("costs.tax_review.tax_inputs")}
              </Text>
              {infoSections.length > 0 ? (
                <InfoButton
                  accessibilityLabel={t("costs.tax_review.view_tax_sources")}
                  testID="tax-review-sources-info-button"
                  onPress={() => setShowInfo(true)}
                />
              ) : null}
            </View>
            <MoonNumericField
              label={t("costs.tax_review.adjusted_gross_income")}
              value={agiDraft}
              onChange={onAgiChange}
              decimals={2}
              min={0}
              prefix={currencyAffixes.prefix}
              suffix={currencyAffixes.suffix}
              placeholder="100000"
              error={inlineInputError ?? undefined}
            />
            <MoonFormField label={t("costs.tax_review.itemization_status")}>
              <InlineOptionPicker
                wrap
                columns={2}
                value={itemizationDraft}
                onChange={handleItemizationChange}
                options={[
                  {
                    id: "yes",
                    label: t("costs.tax_review.itemizes_deductions"),
                  },
                  {
                    id: "no",
                    label: t("costs.tax_review.takes_standard_deduction"),
                  },
                  {
                    id: "unknown",
                    label: t("costs.tax_review.itemization_unknown"),
                  },
                ]}
              />
            </MoonFormField>
          </CardSurface>
        </CardSurface>
        <InfoSheet
          visible={showInfo}
          onClose={() => setShowInfo(false)}
          title={t("costs.tax_review.tax_sources_title")}
          sections={infoSections}
        />
      </View>

      <View>
        <SectionTitleRow
          title={t("costs.tax_review.expenses_in_review")}
          icon={FileText}
          style={{ marginTop: 28 }}
          action={
            summary.entries.length > 0
              ? {
                  label: t("costs.tax_review.add_expense"),
                  onPress: onAddExpense,
                  icon: Plus,
                  showChevron: false,
                }
              : undefined
          }
        />
        <View style={{ gap: 8 }}>
          {summary.entries.length === 0 ? (
            <TaxExpenseEmptyState
              taxYear={summary.taxYear}
              onAddExpense={onAddExpense}
              withCard
              otherYearRecords={(allRecords ?? []).filter(
                (record) =>
                  !record.occurredOn?.startsWith(`${summary.taxYear}-`),
              )}
              onOpenRecord={onOpenPaymentRecord}
              locale={locale}
            />
          ) : (
            orderedEntries.map((entry) => (
              <ReviewEntry
                key={entry.candidate.id}
                entry={entry}
                focused={entry.candidate.id === focusedId}
                onOpenDetails={() => onOpenDetails(entry)}
                locale={locale}
                t={t}
                calculationLineItem={calculationLineItemsByRecordId.get(
                  entry.record.id,
                )}
              />
            ))
          )}
        </View>
      </View>
    </>
  );
}
